import { Component, OnInit } from '@angular/core';
import { FunctionService } from '../services/function.service';
import { PermissionsService } from '../services/permissions.service';
import { DashboardStoreService } from './services/dashboard-store.service';
import { environment } from 'src/environments/environment';
import { CobrancaDash } from './models/cobranca-dash';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { LoaderService } from '../loader/loader.service';
import * as moment from 'moment';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { UsersService } from '../users/users.service';
import { TeamsService } from '../teams/teams.service';
import { EquipeFind } from '../teams/models/equipe-find';
import { User } from '../users/models/user';
import { Equipe } from '../teams/models/equipe';
import { SaleDash } from './models/sale-dash';
import { DashboardService } from './services/dashboard.service';
import { DashFilter } from './models/dash-filter';
import { DateRangeModel } from '../components/datepicker-range/datepicker-i18n.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  env;
  activeTab: string = 'sales';

  fromDate: Date;
  toDate: Date;

  sellersDropdownList = [];
  sellersDropdownSettings: IDropdownSettings = {};
  selectedSellers = [];

  teamsDropdownList = [];
  teamsDropdownSettings: IDropdownSettings = {};
  selectedTeams = [];

  sellers: User[];
  teams: Equipe[];

  private fetchApiDashData$ = new Subject();
  private unsubscribe$ = new Subject();

  constructor(
    private route: ActivatedRoute,
    private func: FunctionService,
    public permissionsService: PermissionsService,
    private dashboardStore: DashboardStoreService,
    private dashboardService: DashboardService,
    private loaderService: LoaderService,
    private usersService: UsersService,
    private teamsService: TeamsService
  ) {
    this.env = environment;
  }

  ngOnInit() {
    this.setFetchApiDashDataObservable();
    this.getDateRangeState();
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      this.setDropdowns();
      this.nextFetchApiDashData();
    });
  }

  async getDashs(cnpj: string, fromDate: Date, toDate: Date) {
    const loader = await this.loaderService.notifica({
      title: 'Buscando dados...',
      date: moment().toISOString(),
      type: 'loading',
    });

    const fromDateFormatted = moment(fromDate).format('YYYY-MM-DD');
    const toDateFormatted = moment(toDate).format('YYYY-MM-DD');

    // const cobrancaDash = this.func
    //   .getCobrancaDashboard(cnpj, fromDateFormatted, toDateFormatted, false)
    //   .then((cobranca: CobrancaDash) => {
    //     this.dashboardStore.addCobranca(cobranca);
    //   }) as Promise<CobrancaDash>;

    const saleDash = await this.getSaleDashData(cnpj, fromDateFormatted, toDateFormatted);

    await Promise.all([saleDash]);

    loader.dismiss();
  }

  async getSaleDashData(cnpj: string, fromDate: string, toDate: string) {
    let supervisorTeams = [];
    let teams = [];
    let sellers = [];

    if (this.permissionsService.isProfile('Supervisor')) {
      supervisorTeams = await this.teamsService.getUserTeams(this.env.user._id).toPromise();
      teams = supervisorTeams.map(team => team._id);
    } else {
      teams = this.selectedTeams.length > 0 ? this.selectedTeams.map(team => team._id) : null;
    }

    sellers = this.selectedSellers.length > 0 ? this.selectedSellers.map(seller => seller.codigo_externo) : null;

    const saleDash = this.dashboardService
      .getSaleDash(cnpj, fromDate, toDate, teams, sellers)
      .toPromise()
      .then((sale: SaleDash) => {
        this.dashboardStore.addSale(sale);
      }) as Promise<SaleDash>;

    return saleDash;
  }

  getDateRangeState() {
    this.fromDate = this.dashboardStore.fromDate;
    this.toDate = this.dashboardStore.toDate;
  }

  onDateRangeChange(dateRange: DateRangeModel) {
    this.fromDate = dateRange.fromDate;
    this.toDate = dateRange.toDate;

    this.dashboardStore.fromDate = this.fromDate;
    this.dashboardStore.toDate = this.toDate;

    this.nextFetchApiDashData();
  }

  refreshDash() {
    if (this.fromDate && this.toDate) {
      this.getDashs(this.env.selected.cnpj, this.fromDate, this.toDate);
    }
  }

  setFetchApiDashDataObservable() {
    this.fetchApiDashData$
      .pipe(
        takeUntil(this.unsubscribe$),
        distinctUntilChanged((p: DashFilter, q: DashFilter) => {
          if (p.cnpj === q.cnpj) {
            if (p.fromDate === q.fromDate) {
              if (p.toDate === q.toDate) {
                if (p.teams.length === q.teams.length) {
                  if (p.sellers.length === q.sellers.length) {
                    return true;
                  }
                }
              }
            }
          }
          return false;
        })
      )
      .subscribe(_ => this.refreshDash());
  }

  nextFetchApiDashData() {
    this.fetchApiDashData$.next({
      cnpj: this.env.selected.cnpj,
      fromDate: this.fromDate,
      toDate: this.toDate,
      teams: this.selectedTeams,
      sellers: this.selectedSellers,
    });
  }

  setDropdowns() {
    this.setSellersDropdown();
    this.setTeamsDropdown();
  }

  async setSellersDropdown() {
    const sellers = await this.getSellers();

    this.sellersDropdownList = sellers;
    this.selectedSellers = [];
    this.sellersDropdownSettings = {
      singleSelection: false,
      idField: 'codigo_externo',
      textField: 'nome',
      selectAllText: 'Selecionar Todos',
      unSelectAllText: 'Limpar Seleção',
      itemsShowLimit: 1,
      allowSearchFilter: true,
      searchPlaceholderText: 'Pesquisar',
      noDataAvailablePlaceholderText: 'Sem dados disponíveis',
    };
  }

  async setTeamsDropdown() {
    this.teams = await this.teamsService.getTeams().toPromise();

    this.teamsDropdownList = this.teams;
    this.selectedTeams = [];
    this.teamsDropdownSettings = {
      singleSelection: false,
      idField: '_id',
      textField: 'nome',
      selectAllText: 'Selecionar Todos',
      unSelectAllText: 'Limpar Seleção',
      itemsShowLimit: 1,
      allowSearchFilter: true,
      searchPlaceholderText: 'Pesquisar',
      noDataAvailablePlaceholderText: 'Sem dados disponíveis',
    };
  }

  async getSellers() {
    const users = await this.usersService.getUsers(this.env.selected.cnpj).toPromise();

    let supervisorTeams: EquipeFind[] = [];
    let supervisorSellers = [];
    let selectedSellers = [];

    if (Array.isArray(users)) {
      this.sellers = users.filter(user => user.novoPerfil.nome.includes('Vendedor'));

      if (this.permissionsService.isProfile('Supervisor')) {
        supervisorTeams = await this.teamsService.getUserTeams(this.env.user._id).toPromise();
        supervisorSellers = supervisorTeams.reduce((users, currentTeam) => {
          return [...users, ...currentTeam.usuarios];
        }, []);
        selectedSellers = this.sellers.reduce((sellers, currentUser) => {
          return supervisorSellers.find(seller => currentUser._id === seller._id) ? [...sellers, currentUser] : sellers;
        }, []);
      } else {
        selectedSellers = this.sellers;
      }
    }

    return selectedSellers;
  }

  onChangeActiveTab(tab: string) {
    this.activeTab = tab;
  }

  async onChangeTeams(teams: any[]) {
    this.selectedSellers = [];
    if (teams.length > 0) {
      const selectedTeams = this.teams.filter(team => teams.find(equipe => equipe._id === team._id));

      if (selectedTeams.length > 0) {
        this.sellersDropdownList = selectedTeams.reduce((sellers, teams) => {
          return [...sellers, ...teams.membros];
        }, []);
      }
    } else {
      this.sellersDropdownList = this.sellers;
    }
  }

  onChangeSellers(sellers: any[]) {
    this.nextFetchApiDashData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    this.fetchApiDashData$.complete();
  }
}
